import React, { useCallback, useRef } from 'react'
import PropTypes from 'prop-types'
import {
  flip, FloatingFocusManager, FloatingPortal, offset, shift,
  useDismiss, useFloating, useInteractions, useListNavigation, useRole
} from '@floating-ui/react'

/*
 * Generic actions popover anchored on a trigger element. Items follow
 * `[{ label, icon, onClick, variant?, dividerBefore? }]`. `variant: 'danger'`
 * paints the row red-600 (red-500 fill + white text/icon on hover);
 * `dividerBefore` draws a separator above the item. `icon` is a FontAwesome
 * class string or an SVG component. Figma reference: node 774:18222.
 *
 * WAI-ARIA menu pattern: role="menu" + role="menuitem", ArrowUp/Down navigation
 * via useListNavigation, Escape and outside-press dismissal via useDismiss
 * (skipping the anchor so the trigger's onClick can toggle the menu closed).
 */

const POPOVER_CLASS = 'tw:z-[1100] tw:bg-white tw:rounded-md! tw:shadow-ds-popover! '
  + 'tw:p-2 tw:min-w-60'
const ITEM_BASE = 'tw:w-full tw:flex tw:items-center tw:gap-2 tw:py-2 tw:px-3 '
  + 'tw:bg-transparent tw:border-0 tw:cursor-pointer tw:text-left tw:text-sm tw:rounded!'
const ITEM_DEFAULT = `${ITEM_BASE} tw:text-gray-900 tw:hover:bg-gray-100 tw:focus-visible:bg-gray-100`
const ITEM_DANGER = `${ITEM_BASE} tw:text-red-600 tw:hover:bg-red-500 tw:hover:text-white `
  + 'tw:focus-visible:bg-red-500 tw:focus-visible:text-white'
const ICON_FA = 'tw:w-3.5 tw:text-center'
const ICON_SVG_DEFAULT = 'tw:shrink-0 tw:text-gray-500'
const ICON_SVG_DANGER = 'tw:shrink-0'
const SEPARATOR_CLASS = 'tw:my-1 tw:h-px tw:bg-gray-300'

const renderItemIcon = item => {
  if (typeof item.icon !== 'string') {
    const Icon = item.icon
    return <Icon className={item.variant === 'danger' ? ICON_SVG_DANGER : ICON_SVG_DEFAULT} />
  }
  return <i aria-hidden="true" className={`${item.icon} ${ICON_FA}`} />
}

const ActionsMenu = ({
  anchorEl = null, isOpen, onClose, ariaLabel, items, placement = 'bottom-end'
}) => {
  const handleOpenChange = useCallback(open => {
    if (!open)
      onClose()
  }, [onClose])

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: handleOpenChange,
    placement,
    middleware: [offset(4), flip(), shift({ padding: 8 })],
    elements: { reference: anchorEl }
  })

  const handleOutsidePress = useCallback(event => !anchorEl?.contains(event.target), [anchorEl])
  const dismissOptions = { outsidePress: handleOutsidePress, escapeKey: true }
  const dismiss = useDismiss(context, dismissOptions)
  const role = useRole(context, { role: 'menu' })

  const listRef = useRef([])
  const [activeIndex, setActiveIndex] = React.useState(null)
  const listNavigation = useListNavigation(context, {
    listRef, activeIndex, onNavigate: setActiveIndex, loop: true
  })

  const { getFloatingProps, getItemProps } = useInteractions([dismiss, role, listNavigation])

  const buildItemRefSetter = index => node => {
    listRef.current[index] = node
  }

  const buildItemClickHandler = item => () => {
    item.onClick()
    onClose()
  }

  if (!isOpen || !anchorEl)
    return null

  return (
    <FloatingPortal>
      <FloatingFocusManager context={context} initialFocus={-1} modal={false}>
        <div
          ref={refs.setFloating}
          aria-label={ariaLabel}
          className={POPOVER_CLASS}
          style={floatingStyles}
          {...getFloatingProps()}
        >
          {items.map((item, index) => {
            const itemClass = item.variant === 'danger' ? ITEM_DANGER : ITEM_DEFAULT
            return (
              <React.Fragment key={item.label}>
                {item.dividerBefore ? <div className={SEPARATOR_CLASS} role="separator" /> : null}
                <button
                  ref={buildItemRefSetter(index)}
                  className={itemClass}
                  role="menuitem"
                  tabIndex={activeIndex === index ? 0 : -1}
                  type="button"
                  {...getItemProps({ onClick: buildItemClickHandler(item) })}
                >
                  {renderItemIcon(item)}
                  <span>{item.label}</span>
                </button>
              </React.Fragment>
            )
          })}
        </div>
      </FloatingFocusManager>
    </FloatingPortal>
  )
}

ActionsMenu.propTypes = {
  anchorEl: PropTypes.instanceOf(Element),
  ariaLabel: PropTypes.string.isRequired,
  isOpen: PropTypes.bool.isRequired,
  items: PropTypes.arrayOf(PropTypes.shape({
    dividerBefore: PropTypes.bool,
    icon: PropTypes.oneOfType([PropTypes.string, PropTypes.elementType]).isRequired,
    label: PropTypes.string.isRequired,
    onClick: PropTypes.func.isRequired,
    variant: PropTypes.oneOf(['default', 'danger'])
  })).isRequired,
  onClose: PropTypes.func.isRequired,
  placement: PropTypes.string
}

ActionsMenu.defaultProps = {
  anchorEl: null,
  placement: 'bottom-end'
}

export default ActionsMenu
